import type { HTMLProps } from 'react';

import clsx from 'clsx';

import styles from './SlideInput.module.scss';

type ContainerProps = HTMLProps<HTMLDivElement> & {
  /**
   * The translated string or <T> element to use when there is an error with value entered into the input.
   */
  errorText?: React.ReactNode;

  /**
   * The state to represent if the input should be disabled.
   */
  isDisabled?: boolean;

  /**
   * The translated string or <T> element to use as the input's label.
   */
  labelText: React.ReactNode;

  /**
   * Hide the label visually, but keep it accessible for screen readers.
   */
  hideLabel?: boolean;

  /**
   * The translated string or <T> element to use as the input's sublabel (e.g. info about password restrictions).
   */
  supportingText?: React.ReactNode;

  /**
   * The translated string to use as the input's placeholder.
   */
  children: React.ReactNode;
};

export const Container = ({
  className,
  errorText,
  isDisabled,
  labelText,
  hideLabel = false,
  name,
  supportingText,
  children,
  ...containerProps
}: ContainerProps) => {
  return (
    <div
      className={clsx(styles.container, className, {
        [styles.error]: Boolean(errorText),
        [styles.disabled]: isDisabled,
      })}
      {...containerProps}
    >
      {labelText && (
        <label className={clsx(styles.label, { [styles.hidden]: hideLabel })} htmlFor={name}>
          {labelText}
        </label>
      )}

      {supportingText ? <div className={clsx(styles['sub-text'])}>{supportingText}</div> : null}

      <div className={styles.input}>
        <div className={clsx(styles.slideInput, { [styles.error]: Boolean(errorText), [styles.disabled]: isDisabled })}>
          {children}
        </div>
        {errorText ? <div className={styles['sub-text']}>{errorText}</div> : null}
      </div>
    </div>
  );
};
